import { useEffect, useRef, useState } from 'react';
import { match, useHistory } from 'react-router-dom';
import usePromise from 'react-use-promise';
import { styled } from 'styled-components';

import { Container } from 'react-bootstrap';

import * as api from '../../../../api';

import Footer from '../../../Schedule/Form/Footer';
import EmbeddedFooter from '../Common/EmbeddedFooter';
import ProviderHeader from './ProviderHeader';
import Insurances from './Insurances';
import Locations from './Locations';
import ProviderInformation from './ProviderInformation';
import Reviews from './Reviews';
import Publications from './Publications';
import ScheduleAppointmentCard from './ScheduleAppointmentCard';
import StickyTabs, { Selected } from './StickyTabs';

import { EmbeddedFluidContainer } from '../Common';

import { useSelector } from 'react-redux';
import * as store from '../../../../reducers/store';

import { Alert, Link, Loader, Typography } from '@vivaahealth/design-system';
import { hasBackgroundInfo } from '../Common/utils';

interface Props {
  match: match<{ providerSlug: string }>;
  landingPageUrl: string;
  sendHeight: () => void;
}

const Details: React.FC<Props> = ({ match, landingPageUrl, sendHeight }) => {
  const embedded = useSelector((store: store.All) => store.embedded);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState<string | null>(
    null,
  );
  const { providerSlug } = match.params;
  const organizationId = useSelector(
    (store: store.All) => store.inbound.organizationId,
  );
  const history = useHistory();

  const aboutRef = useRef<HTMLDivElement>(null);
  const locationsRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const insurancesRef = useRef<HTMLDivElement>(null);
  const publicationsRef = useRef<HTMLDivElement>(null);

  const [provider, error, state] = usePromise(async () => {
    const providerIdentifier = await api.client.PrevisitProvider.getProviderId(
      organizationId,
      providerSlug,
    );
    return api.client.PrevisitProvider.getProvider(
      organizationId,
      providerIdentifier,
    );
  }, [organizationId, providerSlug]);

  useEffect(() => {
    if (
      !!sessionStorage.getItem('provider') ||
      !!sessionStorage.getItem('specialty')
    ) {
      setSelectedProvider(sessionStorage.getItem('provider'));
      setSelectedSpecialty(sessionStorage.getItem('specialty'));
    }
    return () => sessionStorage.clear();
  }, []);

  useEffect(() => {
    if (embedded) {
      sendHeight();
    }
  });

  if (error && !provider) {
    return (
      <LimitedContainer className="">
        {selectedProvider && selectedSpecialty ? (
          <Container className="ps-0">
            <div className="mb-1">
              <Typography variant="headline-large" bold>
                {selectedProvider}
              </Typography>
            </div>
            <Typography variant="title-large">{selectedSpecialty}</Typography>
          </Container>
        ) : null}
        <Alert title="Unable to load profile" variant="danger">
          Please refresh the page or come back later. <br />
          {selectedProvider && selectedSpecialty ? (
            <StyledLink
              onClick={() => window.history.back()}
              className="d-block pt-3"
              small
            >
              Back to search results
            </StyledLink>
          ) : (
            <StyledLink
              onClick={() => history.push(`${landingPageUrl}/search`)}
              className="d-block pt-3"
              small
            >
              See other providers
            </StyledLink>
          )}
        </Alert>
      </LimitedContainer>
    );
  }
  if (state === 'pending') {
    return <Loader label="Loading provider profile..." className="m-auto" />;
  }

  const handleScroll = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref && ref.current) {
      if (embedded) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        window.scrollTo({
          top: ref.current.offsetTop - 4,
          left: 0,
          behavior: 'smooth',
        });
      }
    }
  };

  const handleSelect = (eventKey: Selected) => {
    switch (eventKey) {
      case Selected.About:
        handleScroll(aboutRef);
        break;
      case Selected.Locations:
        handleScroll(locationsRef);
        break;
      case Selected.Reviews:
        handleScroll(reviewsRef);
        break;
      case Selected.Insurances:
        handleScroll(insurancesRef);
        break;
      case Selected.Publications:
        handleScroll(publicationsRef);
        break;
      default:
        handleScroll(aboutRef);
    }
  };

  const content = (
    <>
      <StickyTabs
        hasSection={{
          about:
            !!provider.profile?.about ||
            !!provider.profile?.carePhilosophy ||
            !!provider.profile?.areasOfInterest ||
            Object.values(hasBackgroundInfo(provider)).some(
              (hasInfo) => hasInfo,
            ),
          reviews: !!provider.preference?.reputationId,
          insurances: !!provider.preference?.acceptedPayorWithPlanTypes?.length,
          publications: !!provider.profile?.publications?.length,
        }}
        handleSelect={handleSelect}
      />
      <div className="d-lg-none">
        <ScheduleAppointmentCard provider={provider} />
      </div>
      <ProviderInformation provider={provider} aboutRef={aboutRef} />
      <Locations practices={provider.practices} locationsRef={locationsRef} />
      <Reviews
        provider={provider}
        reviewsRef={reviewsRef}
        organizationId={organizationId}
      />
      <Insurances
        insurances={provider.preference?.acceptedPayorWithPlanTypes?.sort()}
        insurancesRef={insurancesRef}
      />
      <Publications
        publications={provider.profile?.publications}
        publicationsRef={publicationsRef}
      />
    </>
  );

  return (
    <>
      <ProviderHeader provider={provider} />
      {embedded ? (
        <>
          <EmbeddedFluidContainer fluid>{content}</EmbeddedFluidContainer>
          <EmbeddedFooter />
        </>
      ) : (
        <>
          <LimitedContainer>{content}</LimitedContainer>
          <Footer />
        </>
      )}
    </>
  );
};

export default Details;

const LimitedContainer = styled(Container)`
  @media (min-width: 1200px) {
    max-width: 1032px;
  }
  padding-bottom: 48px;
`;

const StyledLink = styled(Link).attrs<{ color: string }>((props) => ({
  color: props.color || 'var(--color-primary)',
}))`
  color: ${(props) => props.color} !important;
  cursor: pointer;
  text-decoration: none;
`;
